/* This example requires Tailwind CSS v2.0+ */
import React, { Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import s from './SelectMenu.module.css';
import { CheckIcon } from '@heroicons/react/outline';

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ');
}

export function SelectMenu<
    T extends { [key: string]: string | number | boolean },
    M extends {
        value: T;
        render: React.FC<{ active: boolean; selected: boolean; disabled: boolean; value: T }>;
    },
>({
    options,
    label,
    labelClassName,
    className = '',
    inputElement,
    placeholder,
    selected,
    setSelected,
    hideCheck,
    disabled = false,
}: {
    options: M[];
    label: React.ReactNode;
    labelClassName?: string;
    className?: string;
    inputElement?: React.FC<{ value: T }>;
    placeholder: string;
    selected: T;
    setSelected: (val: T) => void;
    hideCheck?: boolean;
    disabled?: boolean;
}) {
    const rootLabelClass = classNames(
        s.rootLabelBase,
        labelClassName ? labelClassName : s.rootLabelDefault,
    );

    const rootListbox = classNames(
        s.rootListboxBase,
        s.rootListboxDefault,
        className,
        disabled ? 'disabled:bg-opacity-40 disabled:text-gray-400 disabled:border-opacity-40' : '',
    );

    return (
        <Listbox value={selected} onChange={setSelected} disabled={disabled}>
            {({ open }) => (
                <>
                    <Listbox.Label className={rootLabelClass}>{label}</Listbox.Label>
                    <div className="group mt-1 relative w-full" aria-disabled={disabled}>
                        <Listbox.Button disabled={disabled} className={rootListbox}>
                            {inputElement ? (
                                inputElement({ value: selected })
                            ) : (
                                <span
                                    className={`block truncate ${
                                        selected?.name ? '' : 'text-gray-300'
                                    }`}
                                >
                                    {selected?.name ?? placeholder}
                                </span>
                            )}
                        </Listbox.Button>
                        <Transition
                            show={open}
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Listbox.Options
                                static
                                className="absolute z-1000 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
                            >
                                {options.map(({ value, render }) => (
                                    <Listbox.Option
                                        key={value?.id as any}
                                        disabled={(!!value?.disabled || disabled) ?? false}
                                        className={({ active, selected }) =>
                                            classNames(
                                                active && !selected
                                                    ? 'bg-teal-100'
                                                    : 'text-passport',
                                                selected ? 'bg-teal-100' : '',
                                                'cursor-default relative py-2 pl-8 pr-4',
                                                disabled ? 'text-opacity-40' : '',
                                            )
                                        }
                                        value={value}
                                    >
                                        {({ active, selected, disabled }) => (
                                            <div className="flex">
                                                {render({ active, selected, disabled, value })}
                                                {selected && !hideCheck && (
                                                    <div className="absolute top-0 right-2 bottom-0 flex items-center justify-center">
                                                        <CheckIcon className="fill-current text-teal-600 h-6 w-6" />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Listbox.Option>
                                ))}
                            </Listbox.Options>
                        </Transition>
                    </div>
                </>
            )}
        </Listbox>
    );
}
